/**
 * Ecosystem account helpers
 * One account (TD1 World / Karma AC / Realm / Mindaloo); plan and caps live here.
 */

import { db } from "@/lib/db";
import { getPlanFeatures, type SubscriptionPlan } from "@/lib/subscription/plans";
import type { Account } from "@/types/account";
import {
  DEFAULT_ACCOUNT_PLAN,
  DEFAULT_KARMA_BALANCE,
} from "@/types/account";
import type { PlanFeatures } from "@/lib/subscription/plans";

/**
 * Get or create account for userId (ecosystem-wide identity).
 * Safe to call on client only; db is local until you plug Supabase.
 */
export async function getAccount(userId: string): Promise<Account> {
  if (typeof window === "undefined" || !db?.accounts) {
    return createStubAccount(userId);
  }
  const existing = await db.accounts.get(userId);
  if (existing) return existing;
  const account: Account = {
    id: userId,
    plan: DEFAULT_ACCOUNT_PLAN,
    karmaBalance: DEFAULT_KARMA_BALANCE,
    ecosystem: undefined,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await db.accounts.put(account);
  return account;
}

function createStubAccount(userId: string): Account {
  return {
    id: userId,
    plan: DEFAULT_ACCOUNT_PLAN,
    karmaBalance: DEFAULT_KARMA_BALANCE,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * Get the plan that drives limits for this user.
 * Prefer account.plan; fallback to profilePlan (from AiProfile) when account not yet used.
 */
export function getEffectivePlan(
  accountPlan: SubscriptionPlan | undefined,
  profilePlan?: SubscriptionPlan
): SubscriptionPlan {
  if (accountPlan) return accountPlan;
  if (profilePlan) return profilePlan;
  return DEFAULT_ACCOUNT_PLAN;
}

/**
 * Caps for a plan (messages/day, api calls/day, file uploads/day, media/month).
 * Use this so each AI and profile "knows its cap" from the account plan.
 */
export function getCapsForPlan(plan: SubscriptionPlan): PlanFeatures {
  return getPlanFeatures(plan);
}

/**
 * Update account plan (e.g. after Stripe subscription change).
 */
export async function updateAccountPlan(
  userId: string,
  plan: SubscriptionPlan
): Promise<Account> {
  const account = await getAccount(userId);
  const updated: Account = {
    ...account,
    plan,
    updatedAt: Date.now(),
  };
  if (typeof window !== "undefined" && db?.accounts) {
    await db.accounts.put(updated);
  }
  return updated;
}

/**
 * Update Karma balance (e.g. after top-up or pay-in-karma spend).
 */
export async function updateKarmaBalance(
  userId: string,
  delta: number
): Promise<Account> {
  const account = await getAccount(userId);
  const karmaBalance = Math.max(0, (account.karmaBalance ?? 0) + delta);
  const updated: Account = {
    ...account,
    karmaBalance,
    updatedAt: Date.now(),
  };
  if (typeof window !== "undefined" && db?.accounts) {
    await db.accounts.put(updated);
  }
  return updated;
}

/**
 * Link ecosystem ids (TD1 World, Karma AC, Realm, Google).
 * Call when user signs in or links another product.
 */
export async function linkEcosystemIdentity(
  userId: string,
  ecosystem: Partial<Account["ecosystem"]>
): Promise<Account> {
  const account = await getAccount(userId);
  const updated: Account = {
    ...account,
    ecosystem: {
      ...(account.ecosystem || {}),
      ...ecosystem,
      linkedAt: Date.now(),
    } as NonNullable<Account["ecosystem"]>,
    updatedAt: Date.now(),
  };
  if (typeof window !== "undefined" && db?.accounts) {
    await db.accounts.put(updated);
  }
  return updated;
}
